import { notify, confirmBottom } from './feedback';
import { useEffect, useState } from 'react';
import { selectDebtTransactions } from './debtTransactions';

export default function Debt({ API_BASE }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    async function load() {
      try {
        const response = await fetch(`${API_BASE}/v1/expenses`, { signal: controller.signal });
        if (!response.ok) throw new Error('Could not load debt transactions. Please try again.');
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error('Unexpected transaction response.');
        setRecords(selectDebtTransactions(data));
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [API_BASE, revision]);

  async function markRepaid(record) {
    if (record.id == null) return;
    const confirmed = await confirmBottom(`Mark debt transaction ${record.id} as repaid?`);
    if (!confirmed) return;
    setUpdatingId(record.id);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/v1/expenses/${record.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transfer_status: 'REPAID' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) throw new Error(data.error || 'Could not mark this debt as repaid.');
      setRecords(current => current.map(item => item.id === record.id ? { ...item, transfer_status: 'REPAID' } : item));
      notify(`Debt transaction ${record.id} marked as repaid.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not mark this debt as repaid.', 'error');
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <section>
      <h1 className="content-title">Debt</h1>
      <button type="button" className="contracts-btn-secondary" disabled={loading} onClick={() => setRevision(value => value + 1)}>Refresh</button>
      {loading ? <p role="status">Loading debt transactions...</p> : error ? <p role="alert" className="error-message">{error}</p> : (
        <>
          <p>{records.length} matching transaction{records.length === 1 ? '' : 's'}</p>
          <div className="table-container" role="region" aria-label="Debt transactions; scroll horizontally for more columns" tabIndex={0}>
            <table className="expenses-table">
              <thead><tr>
                <th>Transaction ID</th><th>Date</th><th>Created by / Requester</th><th>Recipient</th>
                <th>Category</th><th>Subcategory</th><th>Item</th><th>Description</th>
                <th>Amount</th><th>Approval status</th><th>Payment status</th><th>Action</th>
              </tr></thead>
              <tbody>
                {records.length ? records.map((record, index) => {
                  const isRepaid = String(record.transfer_status || '').toUpperCase() === 'REPAID';
                  const isUpdating = updatingId === record.id;
                  return (
                    <tr key={record.id ?? `missing-id-${index}`}>
                      <td>{record.id ?? '—'}</td>
                      <td>{record.created_at ? new Date(record.created_at).toLocaleString() : '—'}</td>
                      <td>{record.created_by || 'Not recorded'}</td>
                      <td>{record.recipient_name || record.account_name || '—'}</td>
                      <td>{record.expense_category || '—'}</td>
                      <td>{record.expense_subcategory || '—'}</td>
                      <td>{record.item_name || '—'}</td>
                      <td>{record.description || '—'}</td>
                      <td>{record.currency || 'NGN'} {Number(record.amount || 0).toLocaleString()}</td>
                      <td>{record.status || 'Not recorded'}</td>
                      <td>{record.transfer_status || 'Not recorded'}</td>
                      <td>
                        <button
                          type="button"
                          className="contracts-btn-secondary"
                          disabled={isRepaid || isUpdating || record.id == null}
                          onClick={() => markRepaid(record)}
                        >
                          {isRepaid ? 'Repaid' : isUpdating ? 'Saving...' : 'Mark repaid'}
                        </button>
                      </td>
                    </tr>
                  );
                }) : <tr><td colSpan={12}>No debt or borrowing transactions found.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
